import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useWindowSize } from '@react-hook/window-size';
import { useIntl } from 'react-intl';
import ReactCrop from 'react-image-crop';

import { useTheme } from '../../ThemeContext';

import Modal from '../Modal';

import 'react-image-crop/dist/ReactCrop.css';
import {
  Wrapper,
  Header,
  Title,
  Content,
  PreviewBox,
  PreviewImageBox,
  PreviewImage,
  Footer,
  Button,
} from './PhotoCrop.style';
import { Breakpoints } from '../../assets/Variables.style';

const PhotoCropInner = ({
  handleClose = () => {},
  sourcePhoto = null,
  handlePhoto = () => {},
}) => {
  const imageRef = useRef();
  const intl = useIntl();
  const theme = useTheme();
  const [tempPhoto, setTempPhoto] = useState(undefined);
  const [blob, setBlob] = useState(undefined);
  const [photoOptions] = useState({
    width: 220,
    height: 220,
  });
  const [crop, setCrop] = useState({
    unit: 'px',
    width: 220,
    height: 220,
    aspect: 1 / 1,
  });

  const onImageLoaded = (image) => {
    imageRef.current = image;
  };

  const onCropComplete = (crop) => {
    makeClientCrop(crop, photoOptions);
  };

  const onCropChange = (crop) => {
    setCrop(crop);
  };

  const confirmPhoto = () => {
    handlePhoto({ tempPhoto, blob });
  };

  const makeClientCrop = async (crop, settings) => {
    if (imageRef.current && crop.width && crop.height) {
      const image = await getCroppedImg(
        imageRef.current,
        crop,
        'tempPhoto.jpg',
        settings
      );
      setTempPhoto(image.photo);
      setBlob(image.blob);
    }
  };

  const getCroppedImg = (image, crop, fileName, settings) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = settings.width ? settings.width : crop.width;
    canvas.height = settings.height ? settings.height : crop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      settings.width ? settings.width : crop.width,
      settings.height ? settings.height : crop.height
    );

    return new Promise((resolve) => {
      const imageDataURL = canvas.toDataURL('image/jpeg');
      resolve({ photo: imageDataURL, blob: dataURLtoBlob(imageDataURL) });
    });
  };

  const dataURLtoBlob = (dataURL) => {
    var arr = dataURL.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  return (
    <Wrapper>
      <Header theme={theme.photoCrop.header}>
        <Title theme={theme.photoCrop.title}>
          {intl.formatMessage({
            id: 'PHOTO_CROP.CROP_IMAGE',
          })}
        </Title>
      </Header>
      <Content>
        <PreviewBox>
          {sourcePhoto && (
            <ReactCrop
              src={sourcePhoto}
              crop={crop}
              onImageLoaded={(image) => onImageLoaded(image)}
              onComplete={(crop) => onCropComplete(crop)}
              onChange={(cropOptions) => onCropChange(cropOptions)}
              keepSelection={true}
            />
          )}

          {tempPhoto && (
            <PreviewImageBox>
              <PreviewImage src={tempPhoto} alt={''} />
            </PreviewImageBox>
          )}
        </PreviewBox>
      </Content>
      <Footer>
        <Button
          type='button'
          theme={theme.button.alt}
          onClick={() => {
            handleClose();
          }}
        >
          {intl.formatMessage({
            id: 'PHOTO_CROP.CANCEL',
          })}
        </Button>
        <Button
          type='button'
          theme={theme.button.default}
          disabled={!tempPhoto || !blob}
          onClick={() => {
            if (!(!tempPhoto || !blob)) confirmPhoto();
          }}
        >
          {intl.formatMessage({
            id: 'PHOTO_CROP.SET_PHOTO',
          })}
        </Button>
      </Footer>
    </Wrapper>
  );
};

const PhotoCrop = ({
  handleClose = () => {},
  sourcePhoto = null,
  handlePhoto = () => {},
}) => {
  const [width] = useWindowSize();
  return (
    <Modal
      handleClose={handleClose}
      fullscreen={width <= Breakpoints.md ? true : false}
    >
      <PhotoCropInner
        handleClose={handleClose}
        sourcePhoto={sourcePhoto}
        handlePhoto={handlePhoto}
      />
    </Modal>
  );
};

PhotoCropInner.propsTypes = {
  handleClose: PropTypes.func,
  sourcePhoto: PropTypes.string,
  handlePhoto: PropTypes.func,
};

PhotoCrop.propsTypes = {
  handleClose: PropTypes.func,
  sourcePhoto: PropTypes.string,
  handlePhoto: PropTypes.func,
};

export default PhotoCrop;
